/**
 * Admin state store — centralized state for admin editing mode.
 * Activated via ?admin URL parameter.
 */
import type mapboxgl from 'mapbox-gl';
import type { CountryData } from '../types';

let countries: CountryData[] = [];
let map: mapboxgl.Map | null = null;
const dirtySet = new Set<string>();

/** True if ?admin is present in the URL */
export function hasAdminParam(): boolean {
  return new URLSearchParams(window.location.search).has('admin');
}

const ADMIN_TOKEN_KEY = 'adminToken';

/** Session token issued by /api/admin/login, or null if missing or expired. */
export function getAdminToken(): string | null {
  const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  if (!token) return null;
  const exp = Number(token.split('.')[0]);
  if (!Number.isFinite(exp) || exp < Date.now()) {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    return null;
  }
  return token;
}

export function setAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminSession(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

/** True if the password gate has been passed this session (server-issued token present and unexpired) */
export function isAdminUnlocked(): boolean {
  return getAdminToken() !== null;
}

/** Check if admin mode is fully active (URL param + authenticated) */
export function isAdminMode(): boolean {
  return hasAdminParam() && isAdminUnlocked();
}

/** Store references to countries array and map instance */
export function initAdminState(c: CountryData[], m: mapboxgl.Map): void {
  countries = c;
  map = m;

  // Warn before leaving with unsaved changes
  window.addEventListener('beforeunload', (e) => {
    if (dirtySet.size > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}

export function getCountries(): CountryData[] {
  return countries;
}

export function getMap(): mapboxgl.Map {
  return map!;
}

export function markDirty(iso2: string): void {
  dirtySet.add(iso2);
}

export function markResourcesDirty(): void {
  dirtySet.add('__resources__');
}

export function getDirtyCount(): number {
  return dirtySet.size;
}

export function isDirty(): boolean {
  return dirtySet.size > 0;
}

export function clearDirty(): void {
  dirtySet.clear();
}
